import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { useTest } from '../contexts/TestContext';
import SoundUtils from '../utils/SoundUtils';

const LoadingPage = () => {
  const { setStage, calculateAndSetResult } = useTest();
  const backgroundRef = useRef(null);
  const promptRef = useRef(null);
  const videoContainerRef = useRef(null);
  const videoRef = useRef(null);
  const progressBarRef = useRef(null);
  const progressBarFillRef = useRef(null);
  const [videoError, setVideoError] = useState(false);
  const [showPlayButton, setShowPlayButton] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  
  // 初始動畫及影片處理
  useEffect(() => {
    // 設置背景顏色為黑色
    gsap.to(backgroundRef.current, {
      backgroundColor: "#000000",
      duration: 0.5,
      ease: "power2.inOut"
    });
    
    // 淡入提示文字
    gsap.fromTo(promptRef.current,
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.8, ease: "back.out" }
    );
    
    // 暫停背景音樂
    if (window.pauseBackgroundMusic && typeof window.pauseBackgroundMusic === 'function') {
      window.pauseBackgroundMusic();
    }
    
    // 1.5秒後顯示影片容器
    setTimeout(() => {
      // 淡出提示文字
      gsap.to(promptRef.current, {
        opacity: 0,
        duration: 0.5,
        ease: "power2.inOut",
        onComplete: () => {
          // 顯示影片容器
          if (videoContainerRef.current) {
            gsap.fromTo(videoContainerRef.current, 
              { opacity: 0 },
              { opacity: 1, duration: 0.5, ease: "power2.inOut", onComplete: () => {
                // 顯示播放按鈕
                setShowPlayButton(true);
                
                // 嘗試自動播放（受瀏覽器策略限制可能會失敗）
                if (videoRef.current) {
                  const playPromise = videoRef.current.play();
                  if (playPromise !== undefined) {
                    playPromise.then(() => {
                      setIsPlaying(true);
                      setShowPlayButton(false);
                    }).catch(error => {
                      console.log('自動播放失敗，需要用戶交互:', error);
                      // 保持播放按鈕顯示，等待用戶點擊
                    });
                  }
                }
              }}
            );
          }
        }
      });
    }, 1500);
    
    // 清理函數
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);
  
  // 處理影片播放相關事件
  useEffect(() => {
    const handleVideoEnded = () => {
      console.log("影片播放結束");
      proceedToResults();
    };
    
    const handleVideoError = () => {
      console.error("影片載入失敗");
      setVideoError(true);
      // 影片載入失敗5秒後跳轉
      setTimeout(proceedToResults, 3000);
    };
    
    const handleTimeUpdate = () => {
      if (videoRef.current) {
        const currentTime = videoRef.current.currentTime;
        const duration = videoRef.current.duration;
        if (duration > 0) {
          const progressPercent = (currentTime / duration) * 100;
          setProgress(progressPercent);
          if (progressBarFillRef.current) {
            progressBarFillRef.current.style.width = `${progressPercent}%`;
          }
        }
      }
    };
    
    // 註冊事件監聽
    if (videoRef.current) {
      videoRef.current.addEventListener("ended", handleVideoEnded);
      videoRef.current.addEventListener("error", handleVideoError);
      videoRef.current.addEventListener("timeupdate", handleTimeUpdate);
    }
    
    // 清理函數
    return () => {
      if (videoRef.current) {
        videoRef.current.removeEventListener("ended", handleVideoEnded);
        videoRef.current.removeEventListener("error", handleVideoError);
        videoRef.current.removeEventListener("timeupdate", handleTimeUpdate);
      }
    };
  }, []);
  
  // 影片開始播放時設置安全超時
  useEffect(() => {
    if (isPlaying) {
      // 設置安全超時：16秒後無論如何都會跳轉（保險機制）
      timerRef.current = setTimeout(proceedToResults, 21000);
    }
    
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [isPlaying]);
  
  // 播放影片
  const handlePlayVideo = () => {
    if (videoRef.current) {
      // 播放按鈕音效
      SoundUtils.playButtonClick();
      
      // 嘗試播放影片
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.then(() => {
          setIsPlaying(true);
          setShowPlayButton(false);
        }).catch(error => {
          console.error('影片播放失敗:', error);
          setVideoError(true);
          setTimeout(proceedToResults, 3000);
        });
      }
    }
  };
  
  // 跳轉到結果頁面
  const proceedToResults = () => {
    // 防止重複執行
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    
    // 計算並設置結果
    calculateAndSetResult();
    
    if (videoContainerRef.current) {
      gsap.to(videoContainerRef.current, {
        opacity: 0,
        duration: 0.5,
        ease: "power2.inOut",
        onComplete: () => {
          setStage('result');
          // 離開頁面前嘗試恢復背景音樂
          setTimeout(() => {
            if (window.resumeBackgroundMusic && typeof window.resumeBackgroundMusic === 'function') {
              window.resumeBackgroundMusic();
            }
          }, 100);
        }
      });
    } else {
      setStage('result');
      // 離開頁面前嘗試恢復背景音樂
      setTimeout(() => {
        if (window.resumeBackgroundMusic && typeof window.resumeBackgroundMusic === 'function') {
          window.resumeBackgroundMusic();
        }
      }, 100);
    }
  };
  
  return (
    <div 
      className="loading-page" 
      ref={backgroundRef} 
      style={{ 
        backgroundColor: "#000000",
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        width: '100%'
      }}
    >
      {/* 影片提示 */}
      <div className="video-prompt-container" ref={promptRef}>
        <div className="video-prompt-text" style={{ color: "#ffffff" }}>
          月老正在計算結果<br />
          想邀請你看部預告...
        </div>
      </div>
      
      {/* 影片播放器 */}
      <div 
        className="video-container" 
        ref={videoContainerRef} 
        style={{ 
          opacity: 0, 
          width: '100%',
          maxWidth: '100%',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <div style={{ 
          position: 'relative', 
          width: '100%',
          maxWidth: '100vw',
          marginBottom: '15px'
        }}>
          {showPlayButton && !isPlaying && (
            <div 
              className="play-button" 
              onClick={handlePlayVideo}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 10,
                cursor: 'pointer'
              }}
            >
              <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                <path d="M8 5v14l11-7z"/>
              </svg>
            </div>
          )}
          
          {videoError && (
            <div 
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: '#ffffff',
                textAlign: 'center',
                padding: '20px',
                backgroundColor: 'rgba(0,0,0,0.7)',
                borderRadius: '10px',
                maxWidth: '80%'
              }}
            >
              <p>影片載入失敗，即將跳轉到結果頁面...</p>
            </div>
          )}
          
          <video 
            ref={videoRef}
            className="preview-video"
            controls={false}
            src="/movie.mp4"
            preload="auto"
            playsInline
            muted={false}
            style={{
              width: '100%',
              height: 'auto',
              display: 'block',
              maxHeight: '80vh'
            }}
          />
        </div>
        
        {/* 不可拖動的進度條 - 放在影片外部底部 */}
        <div 
          ref={progressBarRef}
          style={{
            width: '100%',
            maxWidth: '800px',
            height: '6px',
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '3px',
            overflow: 'hidden',
            marginBottom: '15px'
          }}
        >
          <div 
            ref={progressBarFillRef}
            style={{
              height: '100%',
              width: `${progress}%`,
              backgroundColor: 'rgba(255, 58, 94, 1)',
              borderRadius: '3px',
              transition: 'width 0.1s linear'
            }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default LoadingPage;